import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Monitor, ArrowUpRight, ExternalLink, X } from "lucide-react";
import Reveal from "../Reveal";
import { useLanguage } from "../../contexts/LanguageContext";

const SITES = [
  { id: "diomedikal",  title: "DIO Medikal",      url: "https://www.diomedikal.com/",        domain: "diomedikal.com",        color: "from-teal-600 to-emerald-500",   cat: "Medikal" },
  { id: "softcosm",   title: "Soft Cosmetic",     url: "https://softcosmetic.com.tr/",       domain: "softcosmetic.com.tr",   color: "from-rose-500 to-pink-400",      cat: "Güzellik" },
  { id: "puandorg",   title: "Puandorg Müzik",    url: "https://www.puandorgmuzik.com/",     domain: "puandorgmuzik.com",     color: "from-purple-600 to-pink-500",    cat: "E-ticaret" },
];

function PreviewModal({ site, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  React.useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex flex-col items-center justify-center p-4"
      onClick={onClose}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 24 }}
        onClick={(e) => e.stopPropagation()} className="relative w-full max-w-4xl flex flex-col" style={{ maxHeight: "90vh" }}>
        <div className="flex items-center gap-2 bg-mist/90 backdrop-blur rounded-t-2xl px-4 py-3 border border-mist border-b-0">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-red-400" /><span className="h-3 w-3 rounded-full bg-amber-400" /><span className="h-3 w-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 bg-white rounded-lg px-3 py-1 text-xs text-ink/60 truncate border border-mist">🔒 {site.url}</div>
          <a href={site.url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-pink" onClick={(e) => e.stopPropagation()}>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <button type="button" onClick={onClose} className="h-7 w-7 rounded-full bg-ink/10 hover:bg-ink/20 flex items-center justify-center">
            <X className="h-4 w-4 text-ink/60" />
          </button>
        </div>
        <div className="relative flex-1 bg-white rounded-b-2xl overflow-hidden border border-mist border-t-0" style={{ minHeight: 380, maxHeight: "calc(90vh - 52px)" }}>
          {!loaded && !error && (
            <div className="absolute inset-0 flex items-center justify-center gap-3 bg-mist/40">
              <div className="h-6 w-6 rounded-full border-2 border-brand-pink border-t-transparent animate-spin" />
              <span className="text-sm text-ink/60">{site.domain} yükleniyor…</span>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8 text-center">
              <Monitor className="h-10 w-10 text-ink/30" />
              <p className="text-sm text-ink/60">Bu site iframe'de açılmıyor.</p>
              <a href={site.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-brand-pink text-cream px-5 py-2.5 text-sm font-semibold">
                Yeni Sekmede Aç <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}
          {!error && <iframe src={site.url} title={site.title} className="w-full h-full" style={{ minHeight: 380, border: "none" }}
            onLoad={() => setLoaded(true)} onError={() => setError(true)} sandbox="allow-scripts allow-same-origin allow-forms allow-popups" />}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function WebPreviewStrip() {
  const { lang, t } = useLanguage();
  const [preview, setPreview] = useState(null);

  return (
    <section data-testid="web-preview-strip" className="bg-cream py-section border-t border-mist">
      <div className="container-page">
        <Reveal as="div" className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <span className="eyebrow"><Monitor className="h-3 w-3 text-brand-pink" />
              {lang === "tr" ? "WEB TASARIMLARIMIZ" : "WEB DESIGNS"}
            </span>
            <h2 className="mt-4 font-display text-display-2 font-semibold text-ink">
              {lang === "tr" ? "Teslim ettiğimiz " : "Websites we "}
              <span className="gold-foil-text">{lang === "tr" ? "web siteleri." : "delivered."}</span>
            </h2>
          </div>
          <Link to={`/${lang}/services/web-development`}
            className="inline-flex items-center gap-2 rounded-full border-2 border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink hover:border-ink hover:bg-ink hover:text-cream transition-colors self-start sm:self-auto">
            {lang === "tr" ? "Tüm Projeler" : "All Projects"} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {SITES.map((site, i) => (
            <Reveal key={site.id} as="div" delay={i * 0.08}>
              <div className="group rounded-2xl border border-mist bg-white overflow-hidden hover:border-brand-pink/40 hover:shadow-card transition-all duration-300">
                <div className={`h-32 bg-gradient-to-br ${site.color} relative flex items-center justify-center`}>
                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)", backgroundSize: "14px 14px" }} />
                  <div className="relative bg-white/90 rounded-lg p-2 w-28 shadow-lg">
                    <div className="flex items-center gap-1 mb-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-400" /><span className="h-1.5 w-1.5 rounded-full bg-amber-400" /><span className="h-1.5 w-1.5 rounded-full bg-green-400" />
                    </div>
                    <div className="h-1 rounded bg-mist mb-1 w-full" /><div className="h-1 rounded bg-mist mb-1 w-4/5" /><div className="h-1 rounded bg-mist w-3/5" />
                  </div>
                  <span className="absolute top-3 right-3 rounded-full bg-white/90 text-ink/70 text-[0.6rem] font-bold uppercase tracking-[0.14em] px-2 py-0.5">{site.cat}</span>
                </div>
                <div className="p-4">
                  <h3 className="font-display text-h3 font-semibold text-ink">{site.title}</h3>
                  <p className="text-xs text-brand-pink font-mono mt-0.5">{site.domain}</p>
                  <div className="mt-3 flex gap-2">
                    <button type="button" onClick={() => setPreview(site)}
                      className="inline-flex items-center gap-1 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold text-ink hover:border-brand-pink hover:text-brand-pink transition-colors">
                      <Monitor className="h-3 w-3" /> Ön İzle
                    </button>
                    <a href={site.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-full bg-ink text-cream px-3 py-1.5 text-xs font-semibold hover:bg-ink/80 transition-colors">
                      <ArrowUpRight className="h-3 w-3" /> Aç
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <AnimatePresence>{preview && <PreviewModal site={preview} onClose={() => setPreview(null)} />}</AnimatePresence>
    </section>
  );
}
